import { useEffect, useRef, useState } from 'react';

import { makeStyles } from '@material-ui/core/styles';

import { RiCheckLine, RiFileCopyLine } from '@remixicon/react';

import { ButtonIcon } from '@backstage/ui';

import { useAlert } from '@spotify/confidence-core-react';

import { MarkdownContent } from '../MarkdownContent';
import { FeedbackToolbar } from './FeedbackToolbar';

const useStyles = makeStyles({
  root: {
    padding: 'var(--bui-space-2) 0',
    paddingBottom: 'var(--bui-space-12)',
    minWidth: 0,
    position: 'relative',
    '&:hover $actions, &:focus-within $actions': {
      opacity: 1,
    },
  },
  actions: {
    position: 'absolute',
    left: 'calc(var(--bui-space-1) * -1)',
    bottom: 0,
    opacity: 0,
    transition: 'opacity 150ms ease',
    zIndex: 1,
    display: 'flex',
    gap: 0,
  },
});

interface AssistantMessageProps {
  content: string;
  eventName?: string;
}

export const AssistantMessage = ({
  content,
  eventName,
}: AssistantMessageProps) => {
  const classes = useStyles();
  const [copied, setCopied] = useState(false);
  const alert = useAlert();
  const timeoutRef = useRef<ReturnType<typeof setTimeout>>(undefined);

  useEffect(
    () => () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    },
    [],
  );

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(content);
      setCopied(true);
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      timeoutRef.current = setTimeout(() => setCopied(false), 1000);
    } catch (error) {
      alert.post({
        severity: 'error',
        message: 'Could not copy to clipboard.',
      });
    }
  };

  return (
    <div className={classes.root}>
      <MarkdownContent>{content}</MarkdownContent>
      <div className={classes.actions}>
        <ButtonIcon
          variant="tertiary"
          size="small"
          icon={
            copied ? <RiCheckLine size={14} /> : <RiFileCopyLine size={14} />
          }
          onPress={handleCopy}
          aria-label="Copy message as Markdown"
        />
        {eventName && (
          <FeedbackToolbar eventName={eventName} content={content} />
        )}
      </div>
    </div>
  );
};
